import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../lib/auth/axiosApi";
import "./flockout.css";
import { FO_HOME, FO_MAP } from "./paths";
import PublicMap from "./PublicMap";

// Set once the firmware + server repos are public. Until then the page doesn't link anywhere.
const REPO_URL = null;

// ESP Web Tools: flashes the board from the browser over Web Serial (Chrome/Edge, desktop).
const FLASHER_SRC = "https://unpkg.com/esp-web-tools@10/dist/web/install-button.js?module";
const canFlash = typeof navigator !== "undefined" && "serial" in navigator;

// Street prices, rounded, Sept 2026. The whole pitch is "cheap", so keep this honest.
const PARTS = [
  { part: "Seeed XIAO ESP32-S3", why: "Hears drones over WiFi and Bluetooth, and sends them to the map over your WiFi.", price: 8 },
  { part: "2.4 GHz antenna with a U.FL (IPEX) plug", why: "Some boards ship with one, some don't. Without it the box can barely hear your router, let alone a drone.", price: 3 },
  { part: "USB-C cable + any phone charger", why: "You probably have one in a drawer.", price: 5 },
  { part: "Case (3D printed, or a small project box)", why: "Optional. Tape works.", price: 2 },
];

function Stat({ value, label }) {
  return (
    <div className="fo-stat">
      <b>{value === null ? "—" : value.toLocaleString()}</b>
      <span>{label}</span>
    </div>
  );
}

function FlockoutLanding() {
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", city: "", has_hardware: false, note: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const loggedIn = !!localStorage.getItem("access_token");

  useEffect(() => {
    if (canFlash && !document.querySelector(`script[src="${FLASHER_SRC}"]`)) {
      const tag = document.createElement("script");
      tag.type = "module";
      tag.src = FLASHER_SRC;
      document.body.appendChild(tag);
    }
  }, []);

  useEffect(() => {
    document.title = "Get The Flock Out My Sky";
    axiosInstance
      .get("skywatch/public-stats/")
      .then((res) => setStats(res.data))
      .catch(() => setStats(null));
  }, []);

  const set = (field) => (e) =>
    setForm({ ...form, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    axiosInstance
      .post("skywatch/request-access/", form)
      .then(() => setSent(true))
      .catch((err) => {
        if (err.response && err.response.status === 429) {
          setError("Too many requests from here. Try again in an hour.");
        } else if (err.response && err.response.data && err.response.data.email) {
          setError("That email doesn't look right.");
        } else {
          setError("That didn't go through. Check your connection and try again.");
        }
      })
      .finally(() => setSending(false));
  };

  const total = PARTS.reduce((a, p) => a + p.price, 0);

  return (
    <div className="flockout">
      <section className="fo-maphero" aria-label="Live map">
        <div className="fo-maphero-map">
          <PublicMap />
        </div>
        <div className="fo-tape fo-maphero-tape" aria-hidden="true" />
        <nav className="fo-nav fo-maphero-nav">
          <Link className="fo-mark" to={FO_HOME}>gettheflockout.my</Link>
          <div className="fo-links">
            <a href="#kit">The kit</a>
            <a href="#join">Join</a>
            <Link to={FO_MAP}>Big map</Link>
            {!loggedIn && <Link to="/login">Log in</Link>}
          </div>
        </nav>
        <header className="fo-hero fo-maphero-card">
          <h1>
            Get the flock out my sky, <span className="fo-bitch">bitch.</span>
          </h1>
          <p>
            A $15 box in your window that tells you when a drone is over your house, who made it,
            how high it is, and where it went. Every box on the map fills in your neighbors' sky too.
          </p>
          <div className="fo-cta">
            <Link className="fo-btn fo-primary" to={FO_MAP}>Open the big map</Link>
            <a className="fo-btn" href="#kit">Get a box</a>
          </div>
        </header>
        <div className="fo-maphero-legend" aria-label="Legend">
          <div><span className="dot" style={{ background: "#ff2e88" }} />DJI drone + flight path</div>
          <div><span className="dot" style={{ background: "#c77dff" }} />Other drone</div>
          <div><span className="dot" style={{ background: "#ff5b3a" }} />Unidentified object</div>
          <div><span className="dot" style={{ background: "#ffd400" }} />Plate reader</div>
          <div><span className="dot ring" />A member's box (approximate)</div>
          <div className="fo-meta">Last 7 days. Click anything for details.</div>
        </div>
        <a className="fo-maphero-more" href="#how">How it works ↓</a>
      </section>

      <div className="fo-wrap">
        <div className="fo-stats" aria-label="Live network numbers">
          <Stat value={stats ? stats.nodes_online : null} label="Boxes online" />
          <Stat value={stats ? stats.drones_this_week : null} label="Drones this week" />
          <Stat value={stats ? stats.unidentified_this_week : null} label="Unidentified this week" />
          <Stat value={stats ? stats.alpr_cameras : null} label="Plate readers found" />
        </div>

        <p className="fo-note">
          It also sniffs out license plate reader cameras (Flock and friends) around your block. Receive-only, legal, and
          nobody sees your address.
        </p>

        <section className="fo-section" id="how">
          <div className="fo-eyebrow">How it works</div>
          <h2>Plug it in. Watch your sky.</h2>
          <ol className="fo-steps">
            <li>
              <h3>Flash it from your browser</h3>
              <p>Plug the board into your laptop and click one button. No coding.</p>
            </li>
            <li>
              <h3>Put it in a window</h3>
              <p>Join the box's WiFi from your phone, pick your home network, paste your key. It checks in on its own.</p>
            </li>
            <li>
              <h3>Get the map</h3>
              <p>Every drone your box hears shows up on the map: serial, maker, height, flight path. You also see what your neighbors' boxes caught.</p>
            </li>
          </ol>
        </section>

        <section className="fo-section">
          <div className="fo-eyebrow">What it catches, and what it doesn't</div>
          <h2>No magic. Just listening.</h2>
          <div className="fo-grid">
            <div style={{ "--c": "var(--fo-hot)" }}>
              <h3>Drones broadcasting Remote ID</h3>
              <p>Since 2024 the FAA requires most drones to broadcast who and where they are. Your box hears it, usually a few hundred meters out.</p>
            </div>
            <div style={{ "--c": "var(--fo-tape)" }}>
              <h3>License plate readers</h3>
              <p>Some ALPR cameras give themselves away over WiFi or Bluetooth. When yours finds one, it goes on the map for everyone.</p>
            </div>
            <div style={{ "--c": "var(--fo-muted)" }}>
              <h3>Won't catch everything</h3>
              <p>Tiny hobby drones and anyone who switches Remote ID off stay quiet. For those you need the camera upgrade, which flags things in the sky that aren't broadcasting.</p>
            </div>
            <div style={{ "--c": "var(--fo-ok)" }}>
              <h3>Your privacy stays yours</h3>
              <p>Other members see your box as a fuzzy ~500 m circle, never your address. Drone pilots' locations only show to the box that heard them.</p>
            </div>
          </div>
        </section>

        <section className="fo-section" id="kit">
          <div className="fo-eyebrow">The kit</div>
          <h2>About ${total}. Less if you have a charger.</h2>
          <table className="fo-parts">
            <tbody>
              {PARTS.map((p) => (
                <tr key={p.part}>
                  <td>
                    <div>{p.part}</div>
                    <div className="fo-why">{p.why}</div>
                  </td>
                  <td>~${p.price}</td>
                </tr>
              ))}
              <tr className="fo-total">
                <td>Total</td>
                <td>~${total}</td>
              </tr>
            </tbody>
          </table>
          <div className="fo-flash" id="flash">
            <h3>Got the board? Flash it right here.</h3>
            {canFlash ? (
              <>
                <p className="fo-note">
                  Plug it into this computer with a USB-C cable, click the button, pick the board. Takes about a minute.
                  If it doesn't show up, hold the little BOOT button while you plug it in.
                </p>
                <esp-web-install-button manifest="/flockbox/manifest.json">
                  <button slot="activate" className="fo-btn fo-primary" type="button">Install FlockBox</button>
                  <span slot="unsupported" className="fo-note">This browser can't talk to USB devices. Use Chrome or Edge on a computer.</span>
                </esp-web-install-button>
              </>
            ) : (
              <p className="fo-note">Open this page in Chrome or Edge on a computer to flash the board. Phones can't do it.</p>
            )}
          </div>
          <p className="fo-note">
            Want more? A roof node adds plane tracking (ADS-B), a camera pointed at the sky, and a full camera sniffer. It runs
            on a Raspberry Pi, about $250–350.
          </p>
        </section>

        <section className="fo-section" id="join">
          <div className="fo-eyebrow">Join the map</div>
          <h2>Add your box to the map.</h2>
          {sent ? (
            <div className="fo-sent">
              <b>Got it.</b> You'll get an email with a login so you can add your box. In the meantime, go order an ESP32.
            </div>
          ) : (
            <form className="fo-form" onSubmit={submit}>
              <label>
                Name
                <input id="fo-name" required maxLength={100} value={form.name} onChange={set("name")} />
              </label>
              <label>
                Email
                <input id="fo-email" type="email" required value={form.email} onChange={set("email")} />
              </label>
              <label>
                Town / city
                <input id="fo-city" maxLength={100} value={form.city} onChange={set("city")} />
              </label>
              <label className="fo-check">
                <input id="fo-hw" type="checkbox" checked={form.has_hardware} onChange={set("has_hardware")} />
                I already have an ESP32
              </label>
              <label className="fo-full">
                Anything else? (what's been flying over your place)
                <textarea id="fo-note" rows={3} maxLength={500} value={form.note} onChange={set("note")} />
              </label>
              <div className="fo-full">
                <button className="fo-btn fo-primary" type="submit" disabled={sending}>
                  {sending ? "Sending…" : "Let me in"}
                </button>
                {error && <p className="fo-error">{error}</p>}
              </div>
            </form>
          )}
        </section>

        <footer className="fo-footer">
          <div>
            Receive-only. We never jam, spoof, hack, or interfere with anything that flies. That's illegal, and it's not the point.
          </div>
          <div>
            Standing on the shoulders of <a href="https://github.com/colonelpanichacks/flock-you" target="_blank" rel="noopener noreferrer">flock-you</a>,{" "}
            <a href="https://github.com/opendroneid" target="_blank" rel="noopener noreferrer">OpenDroneID</a>, and{" "}
            <a href="https://deflock.me" target="_blank" rel="noopener noreferrer">DeFlock</a>.
            {REPO_URL && <> Our code: <a href={REPO_URL} target="_blank" rel="noopener noreferrer">GitHub</a>.</>}
          </div>
        </footer>
      </div>
    </div>
  );
}

export default FlockoutLanding;
